import React, { useState } from 'react';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useUser } from '../UserContext';

function MapDataUpload({ headers, tableData, locations }) {
    const { user } = useUser();
    const [metadata, setMetadata] = useState({
        email: '',
        notes: ''
    });
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const handleMetadataChange = (e) => {
        const { name, value } = e.target;
        setMetadata(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        if (!tableData.length || !locations.length) {
            setError('No data available to upload');
            return;
        }

        setUploading(true);
        setError('');
        setSuccess('');

        try {
            // Create a structured format for CSV download
            const structuredData = locations.map(location => {
                const rowData = {};
                headers.forEach((header, index) => {
                    rowData[header] = location.data[index];
                });
                rowData.latitude = location.lat;
                rowData.longitude = location.lon;
                return rowData;
            });

            // Upload to Firestore
            await addDoc(collection(db, "testResults"), {
                userId: user?.uid || 'anonymous',
                email: metadata.email,
                notes: metadata.notes,
                data: structuredData,
                headers: [...headers, 'latitude', 'longitude'],
                uploadDate: Timestamp.now(),
                type: 'visualization',
                status: 'pending'
            });

            setSuccess('Data uploaded successfully!');
            setMetadata({
                email: '',
                notes: ''
            });
        } catch (err) {
            console.error('Error uploading data:', err);
            setError('Failed to upload data: ' + err.message);
        } finally {
            setUploading(false);
        }
    };

    if (!tableData.length || !locations.length) {
        return null;
    }

    return (
        <div className="mt-6 p-4 bg-white rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-4">Save Visualization Data</h3>
            
            <form onSubmit={handleUpload} className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address
                    </label>
                    <input
                        type="email"
                        name="email"
                        value={metadata.email}
                        onChange={handleMetadataChange}
                        required
                        className="w-full p-2 border rounded"
                        placeholder="Enter your email"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Additional Notes
                    </label>
                    <textarea
                        name="notes"
                        value={metadata.notes}
                        onChange={handleMetadataChange}
                        className="w-full p-2 border rounded"
                        rows="3"
                        placeholder="Add any relevant notes about this data"
                    />
                </div>

                {error && (
                    <div className="text-red-500 text-sm">{error}</div>
                )}

                {success && (
                    <div className="text-green-500 text-sm">{success}</div>
                )}

                <button
                    type="submit"
                    disabled={uploading}
                    className={`w-full bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600 transition-colors
                        ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                    {uploading ? 'Uploading...' : 'Save Data'}
                </button>
            </form>
        </div>
    );
}

export default MapDataUpload;
